import {CopyPagesState} from '@mintdata-wsp/common/state/ui/visualDesigner/CopyPagesState';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Overlay} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Overlay';
import {IPageSnapshot, Page, PageId, PageName} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {EventProp, IEventPropSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {IPropSnapshot, Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {EventPropName} from '@mintdata/mint-kit/api/events/EventType';
import {EventPropId, PropId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Size} from '@mintdata/mint-kit/api/models/Size';
import {PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';
import {HasEvents, EventBus} from '@mintdata-wsp/common/bus/EventBus';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ISurfaceUiDesignUIState} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignUIState';

export interface ISurfaceStateSnapshot
{
    startPageId:PageId;
    pages:IPagesSnapshot;
    pagesItemsOrdered:PageId[];
    props:{[propId:string]:IPropSnapshot}
    eventProps:{[eventPropId:string]:IEventPropSnapshot}
}

export interface IPagesSnapshot
{
    [pageId:string]:IPageSnapshot;
}

export class SurfaceUiState implements ISurfaceUiDesignUIState
{
    @observable
    public activePageId:PageId = null;

    @observable
    public allowPagesAnimation:boolean = true;

    @observable
    public copyPages:CopyPagesState = new CopyPagesState();

    public toDesignUIState():ISurfaceUiDesignUIState
    {
        return {
            activePageId: this.activePageId
        }
    }

    public fromDesignUIState(state:ISurfaceUiDesignUIState):void
    {
        if( state.activePageId )
            controllers.ui.openDesign.setActivePageById(state.activePageId);
    }
}

type DesignStateListener = {
    onActivePageChanged():void;
}

export class SurfaceState implements HasEvents<DesignStateListener>
{
    @observable
    public startPageId:PageId = null;

    @observable
    public pages:Map<PageId, Page> = new Map();

    @observable
    public pagesItemsOrdered:Page[] = [];

    @observable
    public props:Map<PropId, Prop<any>>;

    @observable
    public eventProps:Map<EventPropId, EventProp>;

    @observable
    public viewportSize:Size = Size.zero();

    public uiState:SurfaceUiState = new SurfaceUiState();

    @computed
    public get propsByName():Map<PropName, Prop<any>>
    {
        const result:Map<PropName, Prop<any>> = new Map();

        this.props.forEach((prop:Prop<any>) => result.set(prop.name, prop));

        return result;
    }

    @computed
    public get eventPropsByName():Map<EventPropName, EventProp>
    {
        const result:Map<EventPropName, EventProp> = new Map();

        this.eventProps.forEach((eventProp:EventProp) => result.set(eventProp.name, eventProp));

        return result;
    }

    private constructor()
    {
    }

    public static create():SurfaceState
    {
        const surfaceState:SurfaceState = new SurfaceState();

        const defaultPage = Page.create('Page 1');
        const viewport:Box = Box.createViewport(defaultPage);
        defaultPage.artboard.addBox(viewport);

        surfaceState.startPageId = defaultPage.id;
        surfaceState.pages = new Map([[defaultPage.id, defaultPage]]);
        surfaceState.pagesItemsOrdered = [defaultPage];

        surfaceState.props = Prop.createSurfaceProps();
        surfaceState.eventProps = EventProp.createSurfaceEventProps();

        surfaceState.uiState.activePageId = defaultPage.id;

        return surfaceState;
    }

    @computed
    public get activePage():Page
    {
        return this.pages.get(this.uiState.activePageId) || (this.pages.size > 0 ? this.pages.values()[0] : null);
    }

    public get allowPagesAnimation():boolean
    {
        return this.uiState.allowPagesAnimation;
    }

    @computed
    public get currentLayerPage():Page
    {
        if( !this.activePage || !this.activePage.activeOverlay )
            return this.activePage;

        const overlay:Overlay = this.activePage.overlays.find((overlay:Overlay) =>
            overlay === this.activePage.activeOverlay
        );

        return overlay.page;
    }

    @action.bound
    public addPage(page:Page):void
    {
        this.pages.set(page.id, page);

        const activePageIndex:number = this.pagesItemsOrdered.findIndex(({id}) => id === this.activePage.id);
        if( activePageIndex > -1 )
            this.pagesItemsOrdered.splice(activePageIndex + 1, 0, this.pages.get(page.id));
        else
            this.pagesItemsOrdered.push(this.pages.get(page.id));

    }

    @action.bound
    public removePage(pageId:PageId):void
    {
        const pageIndex = this.pagesItemsOrdered.findIndex(({id}) => id === pageId);
        this.pagesItemsOrdered.splice(pageIndex, 1);

        this.pages.delete(pageId);
    }

    @action.bound
    public setStartPage(pageId:PageId):void
    {
        this.startPageId = pageId;
    }

    @action.bound
    public renamePage(pageId:PageId, name:PageName):void
    {
        this.pages.get(pageId).name = name;
    }

    @action.bound
    public movePage(pageId:PageId, insertIndex:number):void
    {
        const pageIndex = this.pagesItemsOrdered.findIndex(({id}) => id === pageId);
        if( pageIndex < insertIndex )
            insertIndex--;

        this.pagesItemsOrdered.splice(pageIndex, 1);
        this.pagesItemsOrdered.splice(insertIndex, 0, this.pages.get(pageId));
    }

    @action.bound
    public setActivePage(page:Page):void
    {
        this.uiState.activePageId = page.id;
        EventBus.fire(commonControllers.designState, 'activePageChanged');
    }

    @action.bound
    public setAllowPagesAnimation(allow:boolean):void
    {
        this.uiState.allowPagesAnimation = allow;
    }

    public static toJson:ITransformer<SurfaceState, ISurfaceStateSnapshot> = createTransformer((surfaceState:SurfaceState) =>
    {
        return {
            startPageId: surfaceState.startPageId,
            pages: Array.from(surfaceState.pages.entries()).reduce((result, [pageId, page]) =>
            {
                result[pageId] = Page.toJson(page);
                return result;
            }, {}),
            pagesItemsOrdered: surfaceState.pagesItemsOrdered.map(({id}) => id),
            props: Array.from(surfaceState.props.entries()).reduce((result, [propId, prop]) =>
            {
                const propSnapshot = Prop.toJson(prop);

                if( propSnapshot )
                    result[propId] = propSnapshot;

                return result;
            }, {}),
            eventProps: Array.from(surfaceState.eventProps.entries()).reduce((result, [eventPropId, eventProp]) =>
            {
                const eventPropSnapshot = EventProp.toJson(eventProp);

                if( eventPropSnapshot )
                    result[eventPropId] = eventPropSnapshot;

                return result;
            }, {})
        };
    });

    public static fromJson(json:ISurfaceStateSnapshot,
                           currentState?:SurfaceState,
                           currentJson?:ISurfaceStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):SurfaceState
    {
        if( !currentState )
            return SurfaceState.fromJsonDeep(json);

        if( !currentJson )
            currentJson = SurfaceState.toJson(currentState);

        const surfaceState:SurfaceState = new SurfaceState();

        surfaceState.startPageId = json.startPageId;
        surfaceState.viewportSize = currentState.viewportSize;

        if( json.pages === currentJson.pages )
            surfaceState.pages = currentState.pages;
        else
        {
            const pages:Map<PageId, Page> = new Map();

            Object.entries(json.pages).forEach(([pageId, page]) =>
            {
                const pageCurrentState:Page = currentState.pages.get(pageId);
                const pageCurrentJson:IPageSnapshot = currentJson.pages[pageId];

                if( json.pages[pageId] === currentJson.pages[pageId] )
                    pages.set(pageId, pageCurrentState);
                else
                    pages.set(pageId, Page.fromJson(page, pageCurrentState, pageCurrentJson, ctx));
            });

            pages.forEach((page:Page) => (
                page.overlays.forEach((overlay:Overlay) => overlay.page = pages.get(overlay.page.id))
            ));

            surfaceState.pages = pages;
        }

        surfaceState.pagesItemsOrdered = json.pagesItemsOrdered === currentJson.pagesItemsOrdered
            ? currentState.pagesItemsOrdered
            : json.pagesItemsOrdered.map((pageId:PageId) => surfaceState.pages.get(pageId));

        if( json.props === currentJson.props )
            surfaceState.props = currentState.props;
        else
            surfaceState.props = this._propertiesFromJson(json);

        if( json.eventProps === currentJson.eventProps )
            surfaceState.eventProps = currentState.eventProps;
        else
            surfaceState.eventProps = this._eventPropertiesFromJson(json);

        SurfaceState._restoreUiState(surfaceState, currentState);

        return surfaceState;
    }

    public static fromJsonDeep(json:ISurfaceStateSnapshot):SurfaceState
    {
        const surfaceState:SurfaceState = new SurfaceState();

        surfaceState.startPageId = json.startPageId;

        const pages:Map<PageId, Page> = new Map();

        Object.entries(json.pages).forEach(([pageId, page]) => pages.set(pageId, Page.fromJsonDeep(page)));

        pages.forEach((page:Page) => (
            page.overlays.forEach((overlay:Overlay) => overlay.page = pages.get(overlay.page.id))
        ));

        surfaceState.pages = pages;
        surfaceState.pagesItemsOrdered = json.pagesItemsOrdered.map((pageId:PageId) => pages.get(pageId));

        surfaceState.props = this._propertiesFromJson(json);
        surfaceState.eventProps = this._eventPropertiesFromJson(json);

        surfaceState.uiState.activePageId = surfaceState.startPageId;

        const {bounds} = pages.get(surfaceState.startPageId).artboard.viewport;
        surfaceState.viewportSize = new Size(bounds.width, bounds.height);

        return surfaceState;
    }

    private static _propertiesFromJson(json:ISurfaceStateSnapshot):Map<PropId, Prop<any>>
    {
        const snapshotsByName = {};
        Object.values(json.props).forEach(snapshot =>
            snapshotsByName[snapshot.name] = snapshot
        );

        const props:Map<PropId, Prop<any>> = new Map();

        Prop.getSurfacePropNames().forEach(propName =>
        {
            const propSnapshot = snapshotsByName[propName];
            const prop:Prop<any> = propSnapshot
                ? Prop.fromJsonDeep(propSnapshot, null)
                : Prop.createSurfaceProp<any>(propName);
            props.set(prop.id, prop);
        });

        return props;
    }

    private static _eventPropertiesFromJson(json:ISurfaceStateSnapshot):Map<EventPropId, EventProp>
    {
        const snapshotsByName = {};
        Object.values(json.eventProps).forEach(snapshot =>
            snapshotsByName[snapshot.name] = snapshot
        );

        const eventProps:Map<EventPropId, EventProp> = new Map();

        EventProp.getSurfaceEventPropNames().forEach(eventPropName =>
        {
            const eventPropSnapshot = snapshotsByName[eventPropName];
            const eventProp:EventProp = eventPropSnapshot
                ? EventProp.fromJsonDeep(eventPropSnapshot, null)
                : EventProp.createSurfaceEventProp(eventPropName);

            eventProps.set(eventProp.id, eventProp);
        });

        return eventProps;
    }

    private static _restoreUiState(state:SurfaceState, currentState:SurfaceState):void
    {
        const {activePageId} = currentState.uiState;

        state.uiState.activePageId = activePageId && state.pages.has(activePageId)
            ? activePageId
            : state.startPageId;

        state.uiState.allowPagesAnimation = currentState.uiState.allowPagesAnimation;
    }
}
